import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Card, CardHeader, CardTitle, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Send, Sparkles, Save, Eye, Loader2, RefreshCw,
  ClipboardList, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import api from '@/utils/api';

const humanTimeAgo = (iso) => {
  if (!iso) return 'Never';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days >= 1) return `${days}d ago`;
    const hours = Math.floor(diffMs / 3600000);
    if (hours >= 1) return `${hours}h ago`;
    const mins = Math.floor(diffMs / 60000);
    return `${mins}m ago`;
  } catch (_) { return 'Unknown'; }
};

const AdminEventNudgePage = () => {
  const { id: paramEventId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(paramEventId || search.get('event_id') || '');
  const [event, setEvent] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesVersionTag, setCandidatesVersionTag] = useState(null);
  const [decrementedCount, setDecrementedCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Bundle 67 - Nudge delivery visibility.
  const [sentLog, setSentLog] = useState([]);
  const [sentLogLoading, setSentLogLoading] = useState(false);
  const [sentLogTotal, setSentLogTotal] = useState(0);
  const [sentLogBreakdown, setSentLogBreakdown] = useState({});
  const [sentLogFilter, setSentLogFilter] = useState('all');
  const [testSending, setTestSending] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);

  // Bundle 65 - one-shot bootstrap on mount. Intentionally not
  // re-running when eventId flips (we handle that with a dedicated
  // effect below).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/events?include_drafts=false');
        if (cancelled) return;
        const list = Array.isArray(data?.events) ? data.events : (data || []);
        // Sort by starts_at / event_date ascending.
        const sorted = [...list].sort((a, b) => {
          const ta = new Date(a.event_date || a.starts_at || a.date || 0).getTime();
          const tb = new Date(b.event_date || b.starts_at || b.date || 0).getTime();
          return ta - tb;
        });
        const now = Date.now();
        const upcoming = sorted.find((e) => {
          const t = new Date(e.event_date || e.starts_at || e.date || 0).getTime();
          return t >= now;
        }) || sorted[0];
        setEvents(sorted);
        if (!eventId && upcoming) {
          setEventId(upcoming.id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load events');
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const loadTemplate = useCallback(async (eid) => {
    setTemplateLoading(true);
    try {
      const { data } = await api.get(`/admin/events/${eid}/nudge/template`);
      setSubject(data.subject || '');
      setBody(data.body || '');
      setEvent(data.event || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load template');
    } finally { setTemplateLoading(false); }
  }, []);

  const loadCandidates = useCallback(async (eid, opts = {}) => {
    const isSilent = opts.silent === true;
    if (!isSilent) setCandidatesLoading(true);
    try {
      const { data } = await api.get(
        `/admin/events/${eid}/nudge/candidates?limit=200&offset=0`,
      );
      // Bundle 66 - skip re-render if the version_tag matches; also
      // compute how many candidates dropped off since last poll.
      const newTag = data.version_tag || null;
      const nextTotal = data.total || 0;
      const nextCands = data.candidates || [];
      setCandidatesVersionTag((prevTag) => {
        if (isSilent && prevTag && newTag && prevTag === newTag) {
          return prevTag;
        }
        const removed = Math.max(0, candidatesTotal - nextTotal);
        if (isSilent && removed > 0) {
          setDecrementedCount((n) => n + removed);
          toast.success(
            `${removed} more ${removed === 1 ? 'person' : 'people'} just RSVPed`,
          );
        }
        setCandidates(nextCands);
        setCandidatesTotal(nextTotal);
        if (!isSilent) setSelectedIds(new Set());
        return newTag;
      });
    } catch (e) {
      if (!isSilent) toast.error(e?.response?.data?.detail || 'Failed to load candidates');
    } finally {
      if (!isSilent) setCandidatesLoading(false);
    }
  }, [candidatesTotal]);

  // Bundle 66 - live poll every 20s while the tab is visible.
  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    let timerId = null;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined'
          && document.visibilityState === 'visible') {
        await loadCandidates(eventId, { silent: true });
      }
      if (!cancelled) timerId = window.setTimeout(tick, 20000);
    };
    timerId = window.setTimeout(tick, 20000);
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [eventId, loadCandidates]);

  useEffect(() => {
    if (!eventId) return;
    setSearch({ event_id: eventId }, { replace: true });
    loadTemplate(eventId);
    loadCandidates(eventId);
  }, [eventId, loadTemplate, loadCandidates, setSearch]);

  const eligibleIds = useMemo(
    () => candidates.filter((c) => !c.rate_limited).map((c) => c.id),
    [candidates],
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllSelected = () => {
    setSelectedIds((prev) => {
      if (prev.size >= eligibleIds.length) return new Set();
      return new Set(eligibleIds);
    });
  };

  const doAiDraft = useCallback(async () => {
    if (!eventId) return;
    setAiDrafting(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/nudge/template/ai-draft`,
      );
      setSubject(data.subject || '');
      setBody(data.body || '');
      toast.success('Dost AI draft loaded. Review then save.');
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.error || 'AI draft failed. Try again.',
      );
    } finally { setAiDrafting(false); }
  }, [eventId]);

  const saveTemplate = useCallback(async () => {
    if (!eventId) return;
    setTemplateSaving(true);
    try {
      await api.put(
        `/admin/events/${eventId}/nudge/template`,
        { subject: subject.trim(), body: body.trim() },
      );
      toast.success('Template saved for this event.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setTemplateSaving(false); }
  }, [eventId, subject, body]);

  const doSend = useCallback(async (bodyPayload, label) => {
    setSending(true);
    setBulkConfirmOpen(false);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/nudge/send`, bodyPayload,
      );
      toast.success(
        `${label}: sent ${data.sent}, skipped ${data.skipped_rate_limited} (rate limited), failed ${data.failed?.length || 0}`,
      );
      await loadCandidates(eventId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  }, [eventId, loadCandidates]);

  const sendToSelected = () => doSend(
    { user_ids: Array.from(selectedIds) }, 'Selected',
  );
  const sendToOne = (uid) => doSend({ user_ids: [uid] }, 'One');
  const sendToAll = () => doSend({ scope: 'all_eligible' }, 'All eligible');

  // Bundle 67 - fetch sent-log rows for this event.
  const loadSentLog = useCallback(async (eid, filter = 'all') => {
    if (!eid) return;
    setSentLogLoading(true);
    try {
      const params = { limit: 200, offset: 0 };
      if (filter && filter !== 'all') params.status = filter;
      const { data } = await api.get(
        `/admin/events/${eid}/nudge/sent-log`, { params },
      );
      setSentLog(data.rows || []);
      setSentLogTotal(data.total || 0);
      setSentLogBreakdown(data.status_breakdown || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sent log load failed');
    } finally { setSentLogLoading(false); }
  }, []);

  useEffect(() => {
    if (!eventId) return;
    loadSentLog(eventId, sentLogFilter);
  }, [eventId, sentLogFilter, loadSentLog]);

  // Bundle 67 - domain reputation status card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admin/integrations/resend/domain-status');
        if (!cancelled) setDomainStatus(data);
      } catch (_) { /* silent - not critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bundle 67 - send test to logged-in admin.
  const sendTest = useCallback(async () => {
    if (!eventId) return;
    setTestSending(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/nudge/send-test`,
        {
          subject_override: subject || undefined,
          body_override: body || undefined,
        },
      );
      if (data.sent) {
        toast.success(`Test sent to ${data.to} (msg id ${data.resend_message_id || 'n/a'})`);
      } else {
        toast.error(`Test send failed: ${data.reason || 'unknown'}`);
      }
      await loadSentLog(eventId, sentLogFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setTestSending(false); }
  }, [eventId, subject, body, sentLogFilter, loadSentLog]);

  const compiledPreview = useMemo(() => {
    if (!event) return { subj: '', body: '' };
    const ctx = {
      first_name: 'Jamie',
      event_title: event.title || '',
      city: event.city || '',
      date: event.date || '',
      rsvp_url: `https://dostination.com/events/${event.slug || ''}/rsvp`,
      detail_url: `https://dostination.com/events/${event.slug || ''}`,
    };
    const render = (t) => (t || '').replace(
      /\{(\w+)\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : `{${k}}`),
    );
    return { subj: render(subject), body: render(body) };
  }, [event, subject, body]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-nudge-page">
      <div className="flex items-center justify-between">
        <Button
          size="sm" variant="ghost"
          onClick={() => navigate('/admin/events')}
          data-testid="admin-nudge-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Events
        </Button>
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Mail className="h-4 w-4" /> RSVP Nudge - Bundle 67
        </div>
      </div>

      {domainStatus && domainStatus.severity !== 'green' && (
        <div
          className={`rounded-lg border-l-4 p-3 text-sm flex items-start gap-2 ${domainStatus.severity === 'red' ? 'bg-red-50 border-red-500 text-red-800' : 'bg-yellow-50 border-yellow-500 text-yellow-800'}`}
          data-testid="admin-nudge-domain-warning"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Sender domain not verified</div>
            <div className="text-xs mt-0.5">{domainStatus.summary}</div>
          </div>
        </div>
      )}
      {domainStatus && domainStatus.severity === 'green' && (
        <div
          className="rounded-lg border-l-4 border-success-500 bg-success-50 p-2 text-xs text-success-800 flex items-center gap-2"
          data-testid="admin-nudge-domain-verified"
        >
          <ShieldCheck className="h-4 w-4" />
          Sender domain {domainStatus.detected_domain} is verified in Resend.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Which event?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger className="w-full sm:w-96" data-testid="admin-nudge-event-select">
              <SelectValue placeholder="Pick an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.title || e.slug}
                  {e.city ? ` - ${e.city}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {event && (
            <div className="text-sm text-neutral-600">
              {event.title} in {event.city} on {event.date}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Nudge template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {templateLoading ? (
            <div className="text-sm text-neutral-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading template...
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-neutral-600">Subject</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  data-testid="admin-nudge-subject-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600">Body</label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  maxLength={5000}
                  className="font-mono text-sm"
                  data-testid="admin-nudge-body-textarea"
                />
                <div className="text-[11px] text-neutral-500 mt-1">
                  Available placeholders: <code>{'{first_name}'}</code>, <code>{'{event_title}'}</code>, <code>{'{city}'}</code>, <code>{'{date}'}</code>, <code>{'{rsvp_url}'}</code>, <code>{'{detail_url}'}</code>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm" onClick={doAiDraft} disabled={aiDrafting || !eventId}
                  data-testid="admin-nudge-ai-draft-btn"
                >
                  {aiDrafting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  Regenerate with Dost AI
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={saveTemplate} disabled={templateSaving}
                  data-testid="admin-nudge-save-template-btn"
                >
                  {templateSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save template
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPreviewOpen(true)}
                  data-testid="admin-nudge-preview-btn"
                >
                  <Eye className="h-4 w-4 mr-1" /> Preview
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={sendTest}
                  disabled={testSending || !eventId}
                  data-testid="admin-nudge-send-test-btn"
                >
                  {testSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send test to myself
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            Candidates ({candidatesTotal} unregistered)
            {decrementedCount > 0 && (
              <span className="ml-2 text-xs font-normal text-success-700" data-testid="admin-nudge-decrement-hint">
                (-{decrementedCount} live since you opened this page)
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline" onClick={() => loadCandidates(eventId)}
              disabled={candidatesLoading || !eventId}
              data-testid="admin-nudge-refresh-btn"
            >
              {candidatesLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
            <Button
              size="sm"
              disabled={selectedIds.size === 0 || sending}
              onClick={sendToSelected}
              data-testid="admin-nudge-send-selected-btn"
            >
              <Send className="h-4 w-4 mr-1" /> Send to {selectedIds.size} selected
            </Button>
            <Button
              size="sm" variant="default"
              disabled={eligibleIds.length === 0 || sending}
              onClick={() => setBulkConfirmOpen(true)}
              data-testid="admin-nudge-send-all-btn"
            >
              Nudge all eligible ({eligibleIds.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {candidatesLoading ? (
            <div className="text-sm text-neutral-500 py-6 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading candidates...
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center">
              No unregistered users found for this event.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size >= eligibleIds.length}
                        onCheckedChange={toggleAllSelected}
                        data-testid="admin-nudge-select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Signed up</TableHead>
                    <TableHead>Last active</TableHead>
                    <TableHead>Profile pic</TableHead>
                    <TableHead>Last nudged</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((c) => (
                    <TableRow key={c.id} data-testid={`admin-nudge-row-${c.id}`}>
                      <TableCell>
                        <Checkbox
                          disabled={c.rate_limited}
                          checked={selectedIds.has(c.id)}
                          onCheckedChange={() => toggleSelect(c.id)}
                          data-testid={`admin-nudge-checkbox-${c.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {c.first_name} {c.last_name}
                      </TableCell>
                      <TableCell className="text-sm text-neutral-600">
                        {c.email}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {humanTimeAgo(c.created_at)}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {humanTimeAgo(c.last_login_at)}
                      </TableCell>
                      <TableCell>
                        {c.has_photo ? (
                          <Badge variant="outline" className="bg-success-50 text-success-800">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-neutral-50 text-neutral-500">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {c.rate_limited ? (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-800">
                            {humanTimeAgo(c.last_nudged_at)} (locked 7d)
                          </Badge>
                        ) : (
                          <span className="text-neutral-500">{humanTimeAgo(c.last_nudged_at)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="outline"
                          disabled={c.rate_limited || sending}
                          onClick={() => sendToOne(c.id)}
                          data-testid={`admin-nudge-send-one-${c.id}`}
                        >
                          <Send className="h-3 w-3 mr-1" /> Nudge
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="admin-nudge-sent-log-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Sent log ({sentLogTotal})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={sentLogFilter} onValueChange={setSentLogFilter}>
              <SelectTrigger className="h-8 w-40" data-testid="admin-nudge-sent-log-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="opened">Opened</SelectItem>
                <SelectItem value="clicked">Clicked</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
                <SelectItem value="complained">Complained</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm" variant="outline"
              onClick={() => loadSentLog(eventId, sentLogFilter)}
              disabled={sentLogLoading}
              data-testid="admin-nudge-sent-log-refresh-btn"
            >
              {sentLogLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {Object.keys(sentLogBreakdown).length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs mb-3" data-testid="admin-nudge-sent-log-breakdown">
              {Object.entries(sentLogBreakdown).map(([k, v]) => (
                <Badge key={k} variant="outline" className="bg-neutral-50">
                  {k}: {v}
                </Badge>
              ))}
            </div>
          )}
          {sentLogLoading ? (
            <div className="text-sm text-neutral-500 py-6 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading sent log...
            </div>
          ) : sentLog.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center" data-testid="admin-nudge-sent-log-empty">
              No nudges sent for this event yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Clicked</TableHead>
                    <TableHead>Msg ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sentLog.map((r) => (
                    <TableRow key={r.id} data-testid={`admin-nudge-sent-row-${r.id}`}>
                      <TableCell className="text-sm">
                        <div className="font-medium">{r.first_name} {r.last_name}</div>
                        <div className="text-xs text-neutral-500">{r.email}</div>
                      </TableCell>
                      <TableCell className="text-xs text-neutral-600 max-w-xs truncate">
                        {r.subject_snapshot}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {humanTimeAgo(r.sent_at)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            r.delivery_status === 'delivered' || r.delivery_status === 'opened' || r.delivery_status === 'clicked'
                              ? 'bg-success-50 text-success-800'
                              : r.delivery_status === 'bounced' || r.delivery_status === 'complained' || r.delivery_status === 'failed'
                                ? 'bg-red-50 text-red-800'
                                : 'bg-neutral-50 text-neutral-700'
                          }
                          data-testid={`admin-nudge-sent-status-${r.id}`}
                        >
                          {r.delivery_status || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {r.opened_at ? humanTimeAgo(r.opened_at) : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {r.clicked_at ? humanTimeAgo(r.clicked_at) : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-400 font-mono">
                        {r.resend_message_id ? r.resend_message_id.slice(0, 10) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="admin-nudge-preview-modal">
          <DialogHeader>
            <DialogTitle>Preview (using dummy user Jamie)</DialogTitle>
            <DialogDescription>Actual sends substitute each user&apos;s first name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-neutral-500 uppercase mb-1">Subject</div>
              <div className="border rounded px-3 py-2 bg-neutral-50 font-medium">
                {compiledPreview.subj}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-neutral-500 uppercase mb-1">Body</div>
              <div className="border rounded p-4 bg-white whitespace-pre-wrap font-serif text-[15px] leading-relaxed max-h-[400px] overflow-y-auto">
                {compiledPreview.body}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent data-testid="admin-nudge-bulk-confirm-modal">
          <DialogHeader>
            <DialogTitle>Send nudge to all eligible users?</DialogTitle>
            <DialogDescription>
              This will email {eligibleIds.length} people right now. Anyone nudged
              in the last 7 days is skipped automatically. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendToAll}
              disabled={sending}
              data-testid="admin-nudge-bulk-confirm-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Yes, send to all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventNudgePage;
